import { createFileRoute, Link } from "@tanstack/react-router";
import {
  GraduationCap,
  Users,
  UserCog,
  ClipboardCheck,
  CalendarDays,
  FileText,
  BarChart3,
  Megaphone,
  Award,
  CheckCircle2,
} from "lucide-react";

export const Route = createFileRoute("/guide")({
  head: () => ({
    meta: [
      { title: "Hướng dẫn sử dụng — Ezyclass" },
      {
        name: "description",
        content:
          "Hướng dẫn sử dụng Ezyclass cho 3 vai trò: Quản lý trung tâm, Giáo viên và Học viên.",
      },
      { property: "og:title", content: "Hướng dẫn sử dụng — Ezyclass" },
      {
        property: "og:description",
        content: "Cách dùng Ezyclass cho Admin, Giáo viên và Học viên.",
      },
    ],
    links: [{ rel: "canonical", href: "https://ezyclass.com/guide" }],
  }),
  component: GuidePage,
});

type Step = { icon: any; title: string; desc: string };

const ADMIN_STEPS: Step[] = [
  { icon: UserCog, title: "Thiết lập trung tâm", desc: "Vào Cài đặt → cập nhật tên trung tâm, logo, thông tin liên hệ." },
  { icon: Users, title: "Tạo tài khoản giáo viên & học viên", desc: "Mục Tài khoản → Thêm mới → chọn vai trò, nhập email & mật khẩu." },
  { icon: CalendarDays, title: "Tạo lớp học", desc: "Mục Lớp học → Thêm lớp → chọn giáo viên, học viên, lịch dạy hằng tuần." },
  { icon: BarChart3, title: "Theo dõi báo cáo", desc: "Tổng quan → xem doanh thu, chuyên cần, danh sách lớp đang hoạt động." },
  { icon: Megaphone, title: "Gửi thông báo", desc: "Mục Thông báo → soạn nội dung → chọn nhóm nhận → gửi ngay hoặc hẹn giờ." },
];

const TEACHER_STEPS: Step[] = [
  { icon: CalendarDays, title: "Xem lịch dạy", desc: "Đăng nhập → Trang chủ giáo viên hiển thị các buổi học hôm nay & tuần sau." },
  { icon: ClipboardCheck, title: "Điểm danh", desc: "Vào buổi học → bấm Điểm danh → tick có mặt / vắng cho từng học viên." },
  { icon: FileText, title: "Giao bài tập", desc: "Mục Bài tập → Tạo bài mới → chọn lớp, nhập đề, đính kèm file, đặt hạn nộp." },
  { icon: Award, title: "Chấm điểm & nhận xét", desc: "Mục Chấm bài → mở bài đã nộp → nhập điểm + nhận xét cho từng học viên." },
  { icon: BarChart3, title: "Phân tích tiến bộ", desc: "Mục Phân tích lớp → xem biểu đồ chuyên cần & điểm số từng học viên." },
];

const STUDENT_STEPS: Step[] = [
  { icon: CalendarDays, title: "Xem lịch học", desc: "Trang chủ học viên hiển thị buổi học sắp tới và lịch tuần." },
  { icon: FileText, title: "Làm bài tập", desc: "Mục Bài tập → chọn bài → tải file đề / làm bài trực tiếp → bấm Nộp." },
  { icon: ClipboardCheck, title: "Theo dõi chuyên cần", desc: "Mục Điểm danh → xem các buổi đã có mặt / vắng của bản thân." },
  { icon: Award, title: "Xem kết quả & thành tích", desc: "Mục Lớp học → xem điểm các bài tập, nhận xét của giáo viên, huy hiệu đạt được." },
];

function RoleSection({
  icon: Icon,
  title,
  color,
  steps,
}: {
  icon: any;
  title: string;
  color: string;
  steps: Step[];
}) {
  return (
    <section className="mt-12">
      <div className="flex items-center gap-3 mb-6">
        <div className={`size-11 rounded-xl grid place-items-center text-white ${color}`}>
          <Icon className="size-5" />
        </div>
        <h2 className="font-display text-2xl font-semibold text-slate-900 m-0">{title}</h2>
      </div>
      <ol className="space-y-4">
        {steps.map((s, i) => {
          const SIcon = s.icon;
          return (
            <li
              key={s.title}
              className="flex gap-4 p-4 rounded-2xl border border-slate-200 bg-white"
            >
              <div className="size-10 rounded-lg bg-slate-100 grid place-items-center shrink-0">
                <SIcon className="size-5 text-slate-700" />
              </div>
              <div className="min-w-0">
                <div className="text-[15px] font-semibold text-slate-900">
                  Bước {i + 1}. {s.title}
                </div>
                <div className="text-[14px] text-slate-600 mt-0.5">{s.desc}</div>
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function GuidePage() {
  return (
    <main className="max-w-3xl mx-auto px-5 md:px-8 py-16">
      <Link to="/" className="text-sm text-blue-600 hover:underline">
        ← Về trang chủ
      </Link>
      <h1 className="font-display text-4xl font-bold text-slate-900 mt-3">
        Hướng dẫn sử dụng Ezyclass
      </h1>
      <p className="text-slate-600 mt-2">
        Hướng dẫn nhanh cho 3 vai trò chính: Quản lý trung tâm, Giáo viên và Học viên. Mỗi bước
        đi kèm vị trí menu trong ứng dụng để bạn dễ làm theo.
      </p>

      <div className="mt-6 p-4 rounded-2xl bg-blue-50 border border-blue-100 flex gap-3">
        <CheckCircle2 className="size-5 text-blue-600 shrink-0 mt-0.5" />
        <p className="text-sm text-slate-700 m-0">
          Mẹo: bạn có thể <Link to="/login" className="text-blue-600 underline">đăng nhập bản
          demo</Link> để thử mọi tính năng với dữ liệu mẫu trước khi triển khai thật.
        </p>
      </div>

      <RoleSection
        icon={GraduationCap}
        title="1. Quản lý trung tâm (Admin)"
        color="bg-blue-600"
        steps={ADMIN_STEPS}
      />
      <RoleSection
        icon={Users}
        title="2. Giáo viên"
        color="bg-emerald-600"
        steps={TEACHER_STEPS}
      />
      <RoleSection
        icon={Award}
        title="3. Học viên"
        color="bg-amber-500"
        steps={STUDENT_STEPS}
      />

      <div className="mt-14 p-6 rounded-2xl border border-slate-200 bg-slate-50">
        <div className="font-display text-lg font-semibold text-slate-900">Cần thêm hỗ trợ?</div>
        <p className="text-sm text-slate-600 mt-1">
          Liên hệ với chúng tôi qua email{" "}
          <a href="mailto:infoezyclass@gmail.com" className="text-blue-600 underline">
            infoezyclass@gmail.com
          </a>{" "}
          hoặc gọi <a href="tel:0965108401" className="text-blue-600 underline">0965 108 401</a>.
        </p>
      </div>
    </main>
  );
}
